import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocPartyStrip } from "@/modules/docs/DocPartyStrip";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildSupplierPaymentPrint } from "@/modules/docs/print/doc-print-math";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DataTable } from "@/modules/shell/data-table";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import {
  getSupplierPayment,
  voidSupplierPayment,
} from "@/shared/api/purchasing";
import { supplierPaymentAllocationColumns } from "./po-detail-columns";

const PAY_DOC_TABS = ["payment", "allocations"] as const;

export function SupplierPaymentDetailPage() {
  const { companySlug, paymentId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const { letterhead } = useOrgLetterhead(companySlug);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSupplierPayment>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [docTab, setDocTab] = useDeskDocTab("payment", PAY_DOC_TABS);

  async function reload() {
    if (!companySlug || !paymentId) return;
    setData(await getSupplierPayment(companySlug, paymentId));
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !paymentId) return;
    getSupplierPayment(companySlug, paymentId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, paymentId, navigate]);

  useRecordRecentDoc({
    companySlug,
    kind: "supplierPayment",
    id: data?.payment.id,
    label: data?.payment.number,
    path:
      companySlug && data?.payment.id
        ? `/c/${companySlug}/purchasing/payments/${data.payment.id}`
        : undefined,
    hint: data?.payment.supplierName,
  });

  async function onVoid() {
    if (!companySlug || !paymentId) return;
    const ok = await confirmUi({
      title: "Void this payment?",
      description:
        "AP and cash/bank will reverse. The number is kept (gap OK).",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await voidSupplierPayment(companySlug, paymentId, {
        idempotencyKey: `sp-void-${crypto.randomUUID()}`,
      });
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !paymentId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading payment…</Alert>
        )}
      </PageFrame>
    );
  }

  const { payment: pay, allocations } = data;
  const directionLabel =
    pay.direction === "from_supplier" ? "refund / credit" : "to supplier";
  const printData = buildSupplierPaymentPrint({
    number: pay.number,
    supplierName: pay.supplierName,
    lifecycle: pay.lifecycle,
    direction: pay.direction,
    amount: pay.amount,
    payMethodName: pay.payMethodName,
    payMethodCode: pay.payMethodCode,
    bankRef: pay.bankRef,
    issuedAt: pay.postedAt,
    allocations: allocations.map((a) => ({
      poNumber: a.piNumber ?? a.poNumber ?? "—",
      amount: a.amount,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} letterhead={letterhead} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/purchasing/payments`}
            listLabel="Supplier payments"
          />
        </div>
        <PageHeader
          eyebrow="Supplier payment"
          title={pay.number}
          description={[directionLabel, pay.supplierName]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <RemindMeButton
                companySlug={companySlug}
                kind="supplier_payment"
                docId={paymentId}
                docNumber={pay.number}
                partyName={pay.supplierName}
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {pay.lifecycle === "posted" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void payment
                </Button>
              )}
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={pay.lifecycle} showLabel />
        </div>
        <DocPartyStrip
          companySlug={companySlug}
          partyId={pay.supplierId}
          partyName={pay.supplierName}
          roleLabel="Supplier"
          primaryPhone={pay.supplierPrimaryPhone}
          primaryEmail={pay.supplierPrimaryEmail}
          primaryAddress={pay.supplierPrimaryAddress}
          partyNotes={pay.supplierNotes}
          docNotes={pay.notes}
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="supplier_payment"
          docId={paymentId}
          linkPath={`/c/${companySlug}/purchasing/payments/${paymentId}`}
          docLabel={[pay.number, pay.supplierName].filter(Boolean).join(" · ")}
        />
        <DeskDocTabs
          aria-label="Supplier payment views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "payment", label: "Payment" },
            {
              id: "allocations",
              label: "Allocations",
              count: allocations.length,
            },
          ]}
        />
      </div>

      {docTab === "payment" && (
        <>
          <FieldGroup title="Money" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Amount</dt>
              <dd className="m-0 font-mono font-medium tabular-nums">
                {moneyDisplay(pay.amount)}
              </dd>
              <dt className="text-[var(--color-muted)]">Direction</dt>
              <dd className="m-0">{directionLabel}</dd>
              {(pay.payMethodName || pay.payMethodCode) && (
                <>
                  <dt className="text-[var(--color-muted)]">Method</dt>
                  <dd className="m-0">
                    {pay.payMethodName ?? pay.payMethodCode}
                    {pay.payMethodCode && pay.payMethodName ? (
                      <span className="font-mono text-xs text-[var(--color-muted)]">
                        {" "}
                        · {pay.payMethodCode}
                      </span>
                    ) : null}
                  </dd>
                </>
              )}
              {pay.postedAt ? (
                <>
                  <dt className="text-[var(--color-muted)]">Posted</dt>
                  <dd className="m-0 text-xs">{pay.postedAt}</dd>
                </>
              ) : null}
            </dl>
          </FieldGroup>

          <AdvancedSection className="no-print" subtitle="refs">
            <FieldGroup title="Refs" level="sub">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {pay.bankRef ? (
                  <>
                    <dt className="text-[var(--color-muted)]">Bank / ref</dt>
                    <dd className="m-0">{pay.bankRef}</dd>
                  </>
                ) : (
                  <dd className="m-0 text-[var(--color-muted)]">None set</dd>
                )}
              </dl>
            </FieldGroup>
          </AdvancedSection>
        </>
      )}

      {docTab === "allocations" && (
        <DataTable
          listId="supplier-payment-allocations"
          companySlug={companySlug}
          className="mt-2"
          columns={supplierPaymentAllocationColumns(companySlug)}
          data={allocations}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No allocations"
              description="This payment is not allocated to an order or invoice."
            />
          }
        />
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="supplier_payment"
        docId={paymentId}
        linkPath={`/c/${companySlug}/purchasing/payments/${paymentId}`}
      />
    </PageFrame>
  );
}
